import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Spinner from '../../components/Spinner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Document, Packer, Paragraph, Table, TableRow, TableCell, TextRun, HeadingLevel, WidthType, AlignmentType, BorderStyle } from 'docx';

const TABS = ['Lavori', 'Calcolo SOA', 'Attestazione'];

const fmt = n => n == null ? '—' : Number(n).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true });
const fmtData = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';

export default function SchedaImpresaSOA() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [impresa, setImpresa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [editNome, setEditNome] = useState(false);
  const [formNome, setFormNome] = useState({ ragione_sociale: '', citta: '' });

  function carica() {
    axios.get(`/api/imprese-soa/${id}`)
      .then(r => { setImpresa(r.data); setFormNome({ ragione_sociale: r.data.ragione_sociale, citta: r.data.citta || '' }); })
      .finally(() => setLoading(false));
  }

  useEffect(() => { carica(); }, [id]);

  async function salvaAnagrafica(e) {
    e.preventDefault();
    await axios.put(`/api/imprese-soa/${id}`, formNome);
    setImpresa(i => ({ ...i, ...formNome }));
    setEditNome(false);
  }

  async function elimina() {
    if (!window.confirm(`Eliminare "${impresa.ragione_sociale}" e tutti i suoi lavori?`)) return;
    await axios.delete(`/api/imprese-soa/${id}`);
    navigate('/admin/imprese-soa');
  }

  if (loading) return <Spinner />;
  if (!impresa) return <p className="text-red-600">Ditta non trovata</p>;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <button onClick={() => navigate('/admin/imprese-soa')} className="text-[#2E6096] hover:underline text-sm">← Certificati SOA</button>
          {editNome ? (
            <form onSubmit={salvaAnagrafica} className="flex flex-wrap gap-2 items-end mt-1">
              <input
                required
                value={formNome.ragione_sociale}
                onChange={e => setFormNome(f => ({ ...f, ragione_sociale: e.target.value }))}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
              />
              <input
                value={formNome.citta}
                onChange={e => setFormNome(f => ({ ...f, citta: e.target.value }))}
                placeholder="Città"
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-full sm:w-36 focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
              />
              <button type="submit" className="bg-[#1A3A5C] text-white px-3 py-1.5 rounded-lg text-sm">Salva</button>
              <button type="button" onClick={() => setEditNome(false)} className="text-gray-400 text-sm">Annulla</button>
            </form>
          ) : (
            <>
              <h1 className="text-xl sm:text-2xl font-bold text-[#1A3A5C] mt-1">{impresa.ragione_sociale}</h1>
              <p className="text-gray-500 text-sm">{impresa.citta || '—'}</p>
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {!editNome && (
            <button onClick={() => setEditNome(true)} className="bg-[#1A3A5C] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#2E6096] transition">
              Modifica
            </button>
          )}
          <button onClick={elimina} className="border border-red-300 text-red-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-50 transition">
            Elimina
          </button>
        </div>
      </div>

      {/* Tabs — scrollabili su mobile */}
      <div className="flex gap-1 mb-4 border-b border-gray-200 overflow-x-auto">
        {TABS.map((t, i) => (
          <button
            key={t}
            onClick={() => setTab(i)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition whitespace-nowrap ${tab === i ? 'border-[#1A3A5C] text-[#1A3A5C]' : 'border-transparent text-gray-500 hover:text-[#1A3A5C]'}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 0 && <TabLavoriSOA impresaId={id} />}
      {tab === 1 && <TabCalcoloSOA impresaId={id} impresa={impresa} />}
      {tab === 2 && <TabAttestazione impresaId={id} impresa={impresa} onAggiornata={carica} />}
    </div>
  );
}

// ── Tab Lavori ──────────────────────────────────────────────────────────
const PAGE_SIZE = 15;

function TabLavoriSOA({ impresaId }) {
  const [lavori, setLavori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagina, setPagina] = useState(1);
  const [filtroCat, setFiltroCat] = useState('');
  const [filtroDataDal, setFiltroDataDal] = useState('');
  const [filtroDataAl, setFiltroDataAl] = useState('');
  const [mostraForm, setMostraForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const vuotoForm = { categoria: '', sottocategoria: '', importo: '', data_inizio: '', data_fine: '', ente_appaltante: '', oggetto: '', percentuale_ati: 100, percentuale_sospensione: 0, pubblico: false, escludi: false, lavoro_diretto: false, numero_cel: '', note: '' };
  const [form, setForm] = useState(vuotoForm);
  const [saving, setSaving] = useState(false);

  function carica() {
    axios.get(`/api/imprese-soa/${impresaId}/lavori`)
      .then(r => { setLavori(r.data); setPagina(1); })
      .finally(() => setLoading(false));
  }

  useEffect(() => { carica(); }, [impresaId]);

  function apriNuovo() { setForm(vuotoForm); setEditId(null); setMostraForm(true); }

  function duplica(lav) {
    setForm({
      categoria: lav.categoria || '',
      sottocategoria: lav.sottocategoria || '',
      importo: lav.importo ?? '',
      data_inizio: lav.data_inizio ? lav.data_inizio.slice(0, 10) : '',
      data_fine: lav.data_fine ? lav.data_fine.slice(0, 10) : '',
      ente_appaltante: lav.ente_appaltante || '',
      oggetto: lav.oggetto || '',
      percentuale_ati: lav.percentuale_ati ?? 100,
      percentuale_sospensione: lav.percentuale_sospensione ?? 0,
      pubblico: lav.pubblico ?? false,
      escludi: lav.escludi ?? false,
      lavoro_diretto: lav.lavoro_diretto ?? false,
      numero_cel: lav.numero_cel || '',
      note: lav.note || '',
    });
    setEditId(null); // nessun id → sarà un nuovo inserimento
    setMostraForm(true);
    setTimeout(() => document.getElementById('form-lavoro-soa')?.scrollIntoView({ behavior: 'smooth' }), 50);
  }

  function apriModifica(lav) {
    setForm({
      categoria: lav.categoria || '',
      sottocategoria: lav.sottocategoria || '',
      importo: lav.importo ?? '',
      data_inizio: lav.data_inizio ? lav.data_inizio.slice(0, 10) : '',
      data_fine: lav.data_fine ? lav.data_fine.slice(0, 10) : '',
      ente_appaltante: lav.ente_appaltante || '',
      oggetto: lav.oggetto || '',
      percentuale_ati: lav.percentuale_ati ?? 100,
      percentuale_sospensione: lav.percentuale_sospensione ?? 0,
      pubblico: lav.pubblico ?? false,
      escludi: lav.escludi ?? false,
      lavoro_diretto: lav.lavoro_diretto ?? false,
      numero_cel: lav.numero_cel || '',
      note: lav.note || '',
    });
    setEditId(lav.id);
    setMostraForm(true);
  }

  async function salva(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`/api/imprese-soa/${impresaId}/lavori/${editId}`, form);
      } else {
        await axios.post(`/api/imprese-soa/${impresaId}/lavori`, form);
      }
      setMostraForm(false);
      carica();
    } finally {
      setSaving(false);
    }
  }

  async function elimina(lavId) {
    if (!window.confirm('Eliminare questo lavoro?')) return;
    await axios.delete(`/api/imprese-soa/${impresaId}/lavori/${lavId}`);
    carica();
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={apriNuovo} className="bg-[#1A3A5C] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#2E6096] transition">
          + Aggiungi lavoro
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <input
          type="text"
          placeholder="Filtra per categoria (es. OG1)"
          value={filtroCat}
          onChange={e => { setFiltroCat(e.target.value); setPagina(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096] w-56"
        />
        <input
          type="date"
          value={filtroDataDal}
          onChange={e => { setFiltroDataDal(e.target.value); setPagina(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
        />
        <span className="text-sm text-gray-400 self-center">→</span>
        <input
          type="date"
          value={filtroDataAl}
          onChange={e => { setFiltroDataAl(e.target.value); setPagina(1); }}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
        />
        {(filtroCat || filtroDataDal) && (
          <button
            onClick={() => { setFiltroCat(''); setFiltroDataDal(''); setFiltroDataAl(''); setPagina(1); }}
            className="text-sm text-gray-500 hover:text-gray-800 px-2"
          >
            ✕ Reset
          </button>
        )}
      </div>
      {mostraForm && (
        <form id="form-lavoro-soa" onSubmit={salva} className="bg-white rounded-xl shadow p-5 mb-6">
          <h3 className="font-semibold text-[#1A3A5C] mb-4">{editId ? 'Modifica lavoro' : 'Nuovo lavoro'}</h3>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Campo label="Committente" value={form.ente_appaltante} onChange={v => set('ente_appaltante', v)} />
            <Campo label="Oggetto" value={form.oggetto} onChange={v => set('oggetto', v)} />
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Categoria *</label>
              <input
                required
                value={form.categoria}
                onChange={e => { set('categoria', e.target.value.toUpperCase()); if (e.target.value.toUpperCase() !== 'OG11') set('sottocategoria', ''); }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
              />
            </div>
            {form.categoria.toUpperCase() === 'OG11' ? (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Sottocategoria OG11</label>
                <select
                  value={form.sottocategoria}
                  onChange={e => set('sottocategoria', e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
                >
                  <option value="">— seleziona —</option>
                  <option value="OS3">OS3</option>
                  <option value="OS28">OS28</option>
                  <option value="OS30">OS30</option>
                </select>
              </div>
            ) : <div />}
            <Campo label="Importo €" type="number" value={form.importo} onChange={v => set('importo', v)} required />
            <Campo label="Data inizio" type="date" value={form.data_inizio} onChange={v => set('data_inizio', v)} />
            <Campo label="Data fine" type="date" value={form.data_fine} onChange={v => set('data_fine', v)} />
            <Campo label="% ATI" type="number" value={form.percentuale_ati} onChange={v => set('percentuale_ati', v)} />
            <Campo label="% Sospensione" type="number" value={form.percentuale_sospensione} onChange={v => set('percentuale_sospensione', v)} />
            <Campo label="N° CEL" value={form.numero_cel} onChange={v => set('numero_cel', v)} />
            <Campo label="Note" value={form.note} onChange={v => set('note', v)} />
          </div>
          <div className="flex gap-6 mb-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={form.pubblico} onChange={e => set('pubblico', e.target.checked)} className="rounded" />
              Pubblico
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={form.escludi} onChange={e => set('escludi', e.target.checked)} className="rounded" />
              Escludi dal calcolo
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={form.lavoro_diretto} onChange={e => set('lavoro_diretto', e.target.checked)} className="rounded" />
              Lavoro diretto (20 anni, 10%)
            </label>
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} className="bg-[#1A3A5C] text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              {saving ? 'Salvo...' : 'Salva'}
            </button>
            <button type="button" onClick={() => setMostraForm(false)} className="text-gray-500 text-sm hover:text-gray-700">Annulla</button>
          </div>
        </form>
      )}
       {loading ? <Spinner /> : (() => {
        const lavFiltrati = lavori.filter(l => {
          const catOk = !filtroCat || (l.categoria || '').toLowerCase().includes(filtroCat.toLowerCase());
          const dataOk =
            (!filtroDataDal || (l.data_fine && l.data_fine.slice(0, 10) >= filtroDataDal)) &&
            (!filtroDataAl  || (l.data_fine && l.data_fine.slice(0, 10) <= filtroDataAl));
          return catOk && dataOk;
        });
        const totPagine = Math.max(1, Math.ceil(lavFiltrati.length / PAGE_SIZE));
        const paginaCorrente = Math.min(pagina, totPagine);
        const lavoriPagina = lavFiltrati.slice((paginaCorrente - 1) * PAGE_SIZE, paginaCorrente * PAGE_SIZE);
        return (
          <div className="bg-white rounded-xl shadow overflow-hidden">
            {lavori.length === 0 ? (
              <p className="text-center text-gray-400 py-10 text-sm">Nessun lavoro inserito</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead className="bg-[#EEF4FB] text-[#1A3A5C] font-semibold">
                      <tr>
                        <th className="text-left px-4 py-2">Cat.</th>
                        <th className="text-left px-4 py-2">Sottocat.</th>
                        <th className="text-left px-4 py-2">Committente</th>
                        <th className="text-left px-4 py-2">Oggetto</th>
                        <th className="text-right px-4 py-2">Importo €</th>
                        <th className="text-left px-4 py-2">Inizio</th>
                        <th className="text-left px-4 py-2">Fine</th>
                        <th className="text-left px-4 py-2">N° CEL</th>
                        <th className="text-center px-4 py-2">Escluso</th>
                        <th className="text-center px-4 py-2">Dir.</th>
                        <th className="px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lavoriPagina.map((lav, i) => (
                        <tr key={lav.id} className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${lav.escludi ? 'opacity-50' : ''}`}>
                          <td className="px-4 py-2 font-bold text-[#1A3A5C]">{lav.categoria || '—'}</td>
                          <td className="px-4 py-2 text-gray-500">{lav.sottocategoria || '—'}</td>
                          <td className="px-4 py-2 text-gray-600 max-w-[150px] truncate">{lav.ente_appaltante || '—'}</td>
                          <td className="px-4 py-2 max-w-[200px] truncate">{lav.oggetto || '—'}</td>
                          <td className="px-4 py-2 text-right">{fmt(lav.importo)}</td>
                          <td className="px-4 py-2">{fmtData(lav.data_inizio)}</td>
                          <td className="px-4 py-2">{fmtData(lav.data_fine)}</td>
                          <td className="px-4 py-2 text-gray-500">{lav.numero_cel || '—'}</td>
                          <td className="px-4 py-2 text-center">{lav.escludi ? '✕' : ''}</td>
                          <td className="px-4 py-2 text-center">{lav.lavoro_diretto ? <span className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded font-semibold">Dir.</span> : ''}</td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            <button onClick={() => apriModifica(lav)} className="text-[#2E6096] hover:underline text-xs mr-3">Modifica</button>
                            <button onClick={() => duplica(lav)} className="text-green-600 hover:underline text-xs mr-3">Duplica</button>
                            <button onClick={() => elimina(lav.id)} className="text-red-500 hover:underline text-xs">Elimina</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {totPagine > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
                    <span>{lavFiltrati.length} lavori — pagina {paginaCorrente} di {totPagine}</span>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setPagina(p => Math.max(1, p - 1))}
                        disabled={paginaCorrente === 1}
                        className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                      >‹</button>
                      {Array.from({ length: totPagine }, (_, i) => i + 1).map(n => (
                        <button
                          key={n}
                          onClick={() => setPagina(n)}
                          className={`px-3 py-1 rounded border text-xs font-medium ${n === paginaCorrente ? 'bg-[#1A3A5C] text-white border-[#1A3A5C]' : 'border-gray-200 hover:bg-gray-50'}`}
                        >{n}</button>
                      ))}
                      <button
                        onClick={() => setPagina(p => Math.min(totPagine, p + 1))}
                        disabled={paginaCorrente === totPagine}
                        className="px-3 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
                      >›</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}
    </div>
  );
}

function Campo({ label, value, onChange, type = 'text', required }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type={type}
        step="any"
        required={required}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
      />
    </div>
  );
}

// ── Tab Calcolo SOA ─────────────────────────────────────────────────────
function TabCalcoloSOA({ impresaId, impresa }) {
  const [risultato, setRisultato] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState('');
  const [anni, setAnni] = useState(15);
  const oggi = new Date().toISOString().slice(0, 10);
  const [dataRif, setDataRif] = useState(() => impresa?.data_rif ? new Date(impresa.data_rif).toISOString().slice(0, 10) : oggi);
  const [icp, setIcp] = useState(() => {
    const v = impresa?.incremento_icp;
    return (v != null && v > 0) ? String(v) : '';
  });
  const [icpSalvato, setIcpSalvato] = useState(false);
  const [catIndex, setCatIndex] = useState(0);

  async function salvaIcp(valore, dataRifValue) {
    try {
      await axios.put(`/api/imprese-soa/${impresaId}`, {
        incremento_icp: valore === '' ? 0 : parseFloat(valore) || 0,
        data_rif: dataRifValue ?? null,
      });
      setIcpSalvato(true);
      setTimeout(() => setIcpSalvato(false), 2000);
    } catch { /* silenzioso */ }
  }

  async function calcola() {
    setLoading(true);
    setErrore('');
    try {
      await salvaIcp(icp, dataRif);
      const params = new URLSearchParams({ anni });
      if (dataRif !== oggi) params.set('data_rif', dataRif);
      if (icp !== '') params.set('incremento_icp', icp);
      const r = await axios.get(`/api/imprese-soa/${impresaId}/calcolo?${params}`);
      setRisultato(r.data);
    } catch (err) {
      setErrore(err.response?.data?.error || 'Errore nel calcolo');
    } finally {
      setLoading(false);
    }
  }

  const coloreClassifica = c => c ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';
  const SOGLIE_MAP = { 'I': 258000, 'II': 516000, 'III': 1033000, 'III bis': 1500000, 'IV': 2582000, 'IV bis': 3500000, 'V': 5165000, 'VI': 10329000, 'VII': 15494000, 'VIII': 20658000 };
  const CATEGORIE_SPECIALI = ['OG2', 'OS2A', 'OS2-A', 'OS25'];

  function calcolaNPunta(importiOrd, classifica) {
    const soglia = SOGLIE_MAP[classifica] || 0;
    if (!soglia || !importiOrd.length) return { n: 0, nota: '' };
    if (importiOrd[0] >= soglia * 0.40) return { n: 1, nota: '1 lav. ≥ 40% soglia' };
    const s2 = importiOrd.slice(0, 2).reduce((a, v) => a + v, 0);
    if (importiOrd.length >= 2 && s2 >= soglia * 0.55) return { n: 2, nota: '2 lav. ≥ 55% soglia' };
    const s3 = importiOrd.slice(0, 3).reduce((a, v) => a + v, 0);
    if (importiOrd.length >= 3 && s3 >= soglia * 0.65) return { n: 3, nota: '3 lav. ≥ 65% soglia' };
    return { n: 0, nota: 'nessun lav. di punta' };
  }

  function calcolaNPuntaOG11(importiOrd, classifica, subcat) {
    const soglia = SOGLIE_MAP[classifica] || 0;
    if (!soglia || !importiOrd.length) return 0;
    const isOS3 = subcat === 'OS3';
    const [p1, p2, p3] = isOS3 ? [0.16, 0.22, 0.26] : [0.28, 0.385, 0.455];
    if (importiOrd[0] >= soglia * p1) return 1;
    const s2 = importiOrd.slice(0, 2).reduce((a, v) => a + v, 0);
    if (importiOrd.length >= 2 && s2 >= soglia * p2) return 2;
    const s3 = importiOrd.slice(0, 3).reduce((a, v) => a + v, 0);
    if (importiOrd.length >= 3 && s3 >= soglia * p3) return 3;
    return 0;
  }

  // ── Export DOCX: elenco lavori ──────────────────────────────────────
  async function esportaDOCX() {
    if (!risultato) return;
    const nomeImpresa = impresa?.ragione_sociale || 'Impresa';
    const dataRpt = new Date(risultato.data_report).toLocaleDateString('it-IT');
    const C = { header: '1A3A5C', white: 'FFFFFF', gray: 'F3F4F6', blue: 'EEF4FB' };
    const bn = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
    const noBorders = { top: bn, bottom: bn, left: bn, right: bn };

    function mkCell(text, opts = {}) {
      return new TableCell({
        borders: noBorders,
        shading: opts.bg ? { fill: opts.bg } : undefined,
        children: [new Paragraph({
          alignment: opts.right ? AlignmentType.RIGHT : opts.center ? AlignmentType.CENTER : AlignmentType.LEFT,
          children: [new TextRun({ text: String(text ?? '—'), bold: !!opts.bold, color: opts.color || '000000', size: 18 })],
        })],
      });
    }

    const haICP = risultato.incremento_icp > 0;
    // Colonne: Categoria | Sottocategoria | Committente | Oggetto | Inizio | Fine | Importo € | ICP € | Finale €
    const headers = ['Categoria', 'Sottocategoria', 'Committente', 'Oggetto', 'Inizio', 'Fine', 'Importo €', ...(haICP ? [`ICP ${risultato.incremento_icp}% €`] : []), 'Finale €'];
    const ncols = headers.length;

    // Raggruppa e ordina
    const gruppi = {};
    risultato.lavori.filter(l => !l.escluso).forEach(lav => {
      const cat = (lav.categoria || '—').toUpperCase();
      if (!gruppi[cat]) gruppi[cat] = [];
      gruppi[cat].push(lav);
    });
    Object.values(gruppi).forEach(arr => arr.sort((a, b) => (b.importo_finale || 0) - (a.importo_finale || 0)));

    const tableRows = [
      new TableRow({ children: headers.map(h => mkCell(h, { bg: C.header, bold: true, color: C.white })) }),
    ];

    for (const [cat, lavori] of Object.entries(gruppi).sort(([a], [b]) => a.localeCompare(b))) {
      let totCat = 0;
      lavori.forEach((lav, i) => {
        totCat += lav.importo_finale || 0;
        const bg = i % 2 === 0 ? C.white : C.gray;
        tableRows.push(new TableRow({ children: [
          mkCell(cat, { bg }),
          mkCell(lav.sottocategoria || '—', { bg }),
          mkCell(lav.ente_appaltante || '—', { bg }),
          mkCell(lav.oggetto || '—', { bg }),
          mkCell(lav.data_inizio ? new Date(lav.data_inizio).toLocaleDateString('it-IT') : '—', { bg }),
          mkCell(lav.data_fine ? new Date(lav.data_fine).toLocaleDateString('it-IT') : '—', { bg }),
          mkCell(fmt(lav.importo_originale), { right: true, bg }),
          ...(haICP ? [mkCell(fmt(lav.importo_icp), { right: true, bg })] : []),
          mkCell(fmt(lav.importo_finale), { right: true, bold: true, bg }),
        ] }));
      });
      // Riga totale categoria: label in prima cella, valore nell'ultima
      tableRows.push(new TableRow({ children: [
        mkCell(`Totale ${cat}`, { bold: true, bg: C.blue }),
        ...Array.from({ length: ncols - 2 }, () => mkCell('', { bg: C.blue })),
        mkCell(fmt(totCat), { right: true, bold: true, bg: C.blue }),
      ] }));
    }

    const sections = [
      new Paragraph({ text: `ELENCO LAVORI — ${nomeImpresa}`, heading: HeadingLevel.HEADING_1 }),
      new Paragraph({ children: [new TextRun({ text: `Periodo: ultimi ${risultato.anni || anni} anni — Data riferimento: ${dataRpt}${risultato.ultimo_indice ? ' — Indice ISTAT: ' + risultato.ultimo_indice : ''}`, size: 18, color: '555555' })] }),
      new Paragraph({ text: '' }),
      new Table({ width: { size: 100, type: WidthType.PERCENTAGE }, rows: tableRows }),
    ];

    const doc = new Document({ sections: [{ children: sections }] });
    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `Lavori_SOA_${nomeImpresa.replace(/\s+/g, '_')}.docx`; a.click();
    URL.revokeObjectURL(url);
  }

  // ── Export PDF: calcolo SOA completo ────────────────────────────────
  function esportaPDF() {
    if (!risultato) return;
    const nomeImpresa = impresa?.ragione_sociale || 'Impresa';
    const dataRpt = new Date(risultato.data_report).toLocaleDateString('it-IT');
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const BLU = [26, 58, 92]; const LBLU = [238, 244, 251];
    let y = 15;

    doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(...BLU);
    doc.text(`CALCOLO SOA — ${nomeImpresa}`, 14, y); y += 7;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(80, 80, 80);
    doc.text(`Periodo: ultimi ${risultato.anni || anni} anni — Data riferimento: ${dataRpt}${risultato.ultimo_indice ? ' — Indice ISTAT: ' + risultato.ultimo_indice : ''}${risultato.incremento_icp > 0 ? ` — ICP: ${risultato.incremento_icp}%` : ''}`, 14, y); y += 8;

    // Tabella classifiche
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(...BLU);
    doc.text('Classifiche ottenibili', 14, y); y += 4;
    autoTable(doc, {
      startY: y,
      head: [['Categoria', 'Totale €', 'Lav. punta €', 'N. lav.', 'Classifica', 'Soglia €']],
      body: (() => {
        const rows = risultato.classifiche.map(c => [
          c.categoria, fmt(c.totale), fmt(c.lavoro_punta), c.n_lavori,
          c.classifica ? `Classe ${c.classifica}` : 'Non qualificata',
          c.classifica && SOGLIE_MAP[c.classifica] ? fmt(SOGLIE_MAP[c.classifica]) : '—',
        ]);
        const totSoglie = risultato.classifiche.filter(c => c.classifica && SOGLIE_MAP[c.classifica]).reduce((s, c) => s + SOGLIE_MAP[c.classifica], 0);
        if (totSoglie > 0) rows.push([
          { content: 'Totale attestabile SOA', colSpan: 5, styles: { fontStyle: 'bold', fillColor: BLU, textColor: [255, 255, 255], halign: 'right' } },
          { content: fmt(totSoglie), styles: { fontStyle: 'bold', fillColor: BLU, textColor: [255, 255, 255], halign: 'right' } },
        ]);
        return rows;
      })(),
      headStyles: { fillColor: BLU, textColor: 255, fontSize: 8, fontStyle: 'bold' },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: LBLU },
      columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'right' } },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 8;

    // Dettaglio lavori per categoria
    const gruppi = {};
    risultato.lavori.forEach(lav => {
      const cat = (lav.categoria || '—').toUpperCase();
      if (!gruppi[cat]) gruppi[cat] = { inclusi: [], esclusi: [] };
      if (lav.escluso) gruppi[cat].esclusi.push(lav);
      else gruppi[cat].inclusi.push(lav);
    });
    Object.values(gruppi).forEach(g => g.inclusi.sort((a, b) => (b.importo_finale || 0) - (a.importo_finale || 0)));

    const haICP = risultato.incremento_icp > 0;
    const head = ['N° CEL', 'Committente', 'Oggetto', 'Inizio', 'Fine', 'Originale €', 'Riv. ISTAT €', ...(haICP ? [`ICP €`] : []), 'Finale €'];

    const fmtD = d => d ? new Date(d).toLocaleDateString('it-IT') : '—';
    const rStyle = { fontStyle: 'bold', fillColor: LBLU, textColor: BLU };

    for (const [cat, g] of Object.entries(gruppi).sort(([a], [b]) => a.localeCompare(b))) {
      if (g.inclusi.length === 0) continue;
      if (y > 170) { doc.addPage(); y = 14; }
      const infoCat = risultato.classifiche.find(c => c.categoria === cat);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(...BLU);
      doc.text(`${cat}${infoCat?.classifica ? '  —  Classe ' + infoCat.classifica : ''}`, 14, y); y += 4;

      const isOG11 = cat === 'OG11';

      if (isOG11) {
        const subGruppi = {};
        g.inclusi.forEach(l => { const s = (l.sottocategoria || '—').toUpperCase(); if (!subGruppi[s]) subGruppi[s] = []; subGruppi[s].push(l); });
        const rows = [];
        let totOG11 = 0;
        for (const [sub, lavori] of Object.entries(subGruppi).sort(([a], [b]) => a.localeCompare(b))) {
          rows.push([{ content: sub, colSpan: head.length + 1, styles: { fontStyle: 'bold', fillColor: [200, 220, 240], textColor: BLU } }]);
          let totSub = 0;
          lavori.forEach(lav => {
            totSub += lav.importo_finale || 0;
            rows.push([sub, lav.numero_cel || '—', lav.ente_appaltante || '—', lav.oggetto || '—', fmtD(lav.data_inizio), fmtD(lav.data_fine),
              fmt(lav.importo_originale), fmt(lav.importo_rivalutato), ...(haICP ? [fmt(lav.importo_icp)] : []), fmt(lav.importo_finale)]);
          });
          rows.push([
            { content: `Totale ${sub}`, colSpan: head.length, styles: rStyle },
            { content: fmt(totSub), styles: { ...rStyle, halign: 'right' } },
          ]);
          totOG11 += totSub;
        }
        rows.push([
          { content: 'TOTALE OG11', colSpan: head.length, styles: { fontStyle: 'bold', fillColor: BLU, textColor: [255, 255, 255] } },
          { content: fmt(totOG11), styles: { fontStyle: 'bold', fillColor: BLU, textColor: [255, 255, 255], halign: 'right' } },
        ]);
        autoTable(doc, {
          startY: y, head: [['Sottocat.', ...head]], body: rows,
          headStyles: { fillColor: BLU, textColor: 255, fontSize: 7, fontStyle: 'bold' },
          bodyStyles: { fontSize: 7 }, alternateRowStyles: { fillColor: LBLU },
          columnStyles: { 6: { halign: 'right' }, 7: { halign: 'right' }, 8: { halign: 'right' }, ...(haICP ? { 9: { halign: 'right' } } : {}) },
          margin: { left: 14, right: 14 },
        });
      } else {
        let totCat = 0;
        const rows = g.inclusi.map(lav => {
          totCat += lav.importo_finale || 0;
          return [lav.numero_cel || '—', lav.ente_appaltante || '—', lav.oggetto || '—', fmtD(lav.data_inizio), fmtD(lav.data_fine),
            fmt(lav.importo_originale), fmt(lav.importo_rivalutato), ...(haICP ? [fmt(lav.importo_icp)] : []), fmt(lav.importo_finale)];
        });
        rows.push([
          { content: `Totale ${cat}`, colSpan: head.length - 1, styles: rStyle },
          { content: fmt(totCat), styles: { ...rStyle, halign: 'right' } },
        ]);
        autoTable(doc, {
          startY: y, head: [head], body: rows,
          headStyles: { fillColor: BLU, textColor: 255, fontSize: 7, fontStyle: 'bold' },
          bodyStyles: { fontSize: 7 }, alternateRowStyles: { fillColor: LBLU },
          columnStyles: { 5: { halign: 'right' }, 6: { halign: 'right' }, 7: { halign: 'right' }, ...(haICP ? { 8: { halign: 'right' } } : {}) },
          margin: { left: 14, right: 14 },
        });
      }
      y = doc.lastAutoTable.finalY + 6;
    }

    // Footer: numero pagina + data scaricamento
    const nPagine = doc.getNumberOfPages();
    const pW = doc.internal.pageSize.getWidth();
    const pH = doc.internal.pageSize.getHeight();
    const oggi = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    for (let i = 1; i <= nPagine; i++) {
      doc.setPage(i);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(160, 160, 160);
      doc.text(`Pagina ${i} di ${nPagine}`, pW / 2, pH - 6, { align: 'center' });
      doc.text(`Scaricato il ${oggi}`, pW - 14, pH - 6, { align: 'right' });
    }

    doc.save(`CalcoloSOA_${nomeImpresa.replace(/\s+/g, '_')}.pdf`);
  }

  return (
    <div>
      {/* Controlli */}
      <div className="flex items-end gap-4 mb-6 flex-wrap">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Periodo lavori</label>
          <select value={anni} onChange={e => setAnni(parseInt(e.target.value))} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]">
            <option value={15}>Ultimi 15 anni</option>
            <option value={10}>Ultimi 10 anni</option>
            <option value={5}>Ultimi 5 anni</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Data di riferimento</label>
          <input type="date" value={dataRif} onChange={e => setDataRif(e.target.value)} className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            ICP %{' '}
            {icpSalvato
              ? <span className="text-green-600 font-normal">✓ salvato</span>
              : <span className="text-gray-400">(opzionale)</span>}
          </label>
          <input type="number" min="0" max="100" step="1" value={icp} onChange={e => setIcp(e.target.value)} placeholder="es. 15" className="w-28 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]" />
        </div>
        <button onClick={calcola} disabled={loading} className="bg-[#1A3A5C] text-white px-6 py-2 rounded-lg font-medium hover:bg-[#2E6096] transition disabled:opacity-50">
          {loading ? 'Calcolo...' : 'Esegui calcolo SOA'}
        </button>
        {risultato && (<>
          <button onClick={esportaDOCX} className="border border-[#1A3A5C] text-[#1A3A5C] px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#EEF4FB] transition">
            📄 Elenco lavori DOCX
          </button>
          <button onClick={esportaPDF} className="border border-[#1A3A5C] text-[#1A3A5C] px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#EEF4FB] transition">
            🖨️ Calcolo SOA PDF
          </button>
        </>)}
        {risultato && (
          <span className="text-xs text-gray-400 self-end pb-2">
            Calcolato il {new Date(risultato.data_report).toLocaleDateString('it-IT')} — periodo {anni} anni
            {risultato.ultimo_indice && ` — indice ISTAT: ${risultato.ultimo_indice}`}
          </span>
        )}
      </div>

      {errore && <p className="text-red-600 text-sm mb-4">{errore}</p>}
      {loading && <Spinner />}

      {risultato && !loading && (
        <div className="space-y-6">
          {/* Classifiche */}
          <div className="bg-white rounded-xl shadow p-5">
            <h3 className="font-bold text-[#1A3A5C] mb-4">Classifiche ottenibili per categoria</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#EEF4FB] text-[#1A3A5C] font-semibold">
                  <tr>
                    <th className="text-left px-4 py-2">Categoria</th>
                    <th className="text-right px-4 py-2">Totale €</th>
                    <th className="text-right px-4 py-2">Lav. punta €</th>
                    <th className="text-left px-4 py-2">Regola punta</th>
                    <th className="text-center px-4 py-2">N. lav.</th>
                    <th className="text-center px-4 py-2">Classifica</th>
                    <th className="text-right px-4 py-2">Soglia €</th>
                  </tr>
                </thead>
                <tbody>
                  {risultato.classifiche.map((cat, i) => (
                    <tr key={cat.categoria} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-2.5 font-bold text-[#1A3A5C]">{cat.categoria}</td>
                      <td className="px-4 py-2.5 text-right">{fmt(cat.totale)}</td>
                      <td className="px-4 py-2.5 text-right">{fmt(cat.lavoro_punta)}</td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">{cat.regola || '—'}</td>
                      <td className="px-4 py-2.5 text-center">{cat.n_lavori}</td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${coloreClassifica(cat.classifica)}`}>
                          {cat.classifica ? `Class. ${cat.classifica}` : 'Non qualificata'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-600">{cat.classifica && SOGLIE_MAP[cat.classifica] ? fmt(SOGLIE_MAP[cat.classifica]) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
                {(() => {
                  const totSoglie = risultato.classifiche.filter(c => c.classifica && SOGLIE_MAP[c.classifica]).reduce((s, c) => s + SOGLIE_MAP[c.classifica], 0);
                  return totSoglie > 0 ? (
                    <tfoot>
                      <tr className="bg-[#1A3A5C] text-white font-bold">
                        <td colSpan={6} className="px-4 py-2 text-right text-sm">Totale attestabile SOA</td>
                        <td className="px-4 py-2 text-right text-sm">{fmt(totSoglie)}</td>
                      </tr>
                    </tfoot>
                  ) : null;
                })()}
              </table>
            </div>
          </div>


          {/* ICP */}
          {risultato.incremento_icp > 0 && (
            <div className="bg-white rounded-xl shadow p-5">
              <p className="text-xs text-gray-500 mb-0.5">ICP applicato</p>
              <p className="text-3xl font-bold text-[#2E6096]">{risultato.incremento_icp}%</p>
            </div>
          )}

          {/* Dettaglio lavori per categoria */}
          <div className="bg-white rounded-xl shadow p-5">
            {(() => {
              const gruppi = {};
              risultato.lavori.forEach(lav => {
                const cat = lav.categoria || '—';
                if (!gruppi[cat]) gruppi[cat] = { inclusi: [], esclusi: [] };
                if (lav.escluso) gruppi[cat].esclusi.push(lav);
                else gruppi[cat].inclusi.push(lav);
              });
              Object.values(gruppi).forEach(g => g.inclusi.sort((a, b) => (b.importo_finale || 0) - (a.importo_finale || 0)));
              const classificaMap = {};
              risultato.classifiche.forEach(c => { classificaMap[c.categoria] = c; });
              const catList = Object.keys(gruppi).sort((a, b) => a.localeCompare(b));
              const idxSafe = Math.min(catIndex, catList.length - 1);
              const catAtt = catList[idxSafe];

              return (
                <>
                  {/* Barra navigazione superiore */}
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <h3 className="font-bold text-[#1A3A5C] mr-auto">Dettaglio lavori per categoria</h3>
                    <span className="text-xs text-gray-400">{idxSafe + 1} / {catList.length}</span>
                    <button onClick={() => setCatIndex(i => Math.max(0, i - 1))} disabled={idxSafe === 0}
                      className="px-3 py-1 rounded-lg border border-gray-300 text-sm disabled:opacity-30 hover:bg-gray-50 transition">‹</button>
                    <select value={catAtt} onChange={e => setCatIndex(catList.indexOf(e.target.value))}
                      className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]">
                      {catList.map(c => (
                        <option key={c} value={c}>{c} ({gruppi[c].inclusi.length} lav.)</option>
                      ))}
                    </select>
                    <button onClick={() => setCatIndex(i => Math.min(catList.length - 1, i + 1))} disabled={idxSafe === catList.length - 1}
                      className="px-3 py-1 rounded-lg border border-gray-300 text-sm disabled:opacity-30 hover:bg-gray-50 transition">›</button>
                  </div>

                  {/* Categoria corrente */}
                  {[catAtt].filter(Boolean).map(cat => {
              const g = gruppi[cat];
                const info = classificaMap[cat];
                const isOG11 = cat.toUpperCase() === 'OG11';
                const isSpeciale = !isOG11 && CATEGORIE_SPECIALI.some(c => cat.toUpperCase().replace(/[\s-]/g, '') === c.replace('-', ''));

                // ── OG11: raggruppamento per sottocategoria ──────────────
                if (isOG11) {
                  const subGruppi = {};
                  g.inclusi.forEach(lav => {
                    const sub = (lav.sottocategoria || '—').toUpperCase();
                    if (!subGruppi[sub]) subGruppi[sub] = [];
                    subGruppi[sub].push(lav);
                  });
                  Object.values(subGruppi).forEach(arr => arr.sort((a, b) => (b.importo_finale || 0) - (a.importo_finale || 0)));

                  return (
                    <div key={cat} className="mb-6">
                      <div className="flex items-center gap-3 mb-3">
                        <span className="font-bold text-[#1A3A5C] text-sm">OG11</span>
                        {info && <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${coloreClassifica(info.classifica)}`}>{info.classifica ? `Class. ${info.classifica}` : 'Non qualificata'}</span>}
                      </div>
                      {Object.entries(subGruppi).sort(([a], [b]) => a.localeCompare(b)).map(([subcat, lavori]) => {
                        const subcatInfo = info?.sottocategorie?.find(s => s.sottocategoria === subcat);
                        const importiSubOrd = lavori.map(l => l.importo_finale || 0);
                        const nPuntaSub = calcolaNPuntaOG11(importiSubOrd, subcatInfo?.classifica, subcat);
                        let progSub = 0;
                        return (
                          <div key={subcat} className="mb-4 ml-4 border-l-2 border-[#2E6096] pl-4">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-semibold text-[#2E6096] text-sm">{subcat}</span>
                              {subcatInfo && <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${coloreClassifica(subcatInfo.classifica)}`}>{subcatInfo.classifica ? `Class. ${subcatInfo.classifica}` : 'Non qualificata'}</span>}
                            </div>
                            <div className="overflow-x-auto">
                            <table className="w-full text-xs min-w-[600px]">
                              <thead className="bg-[#EEF4FB] text-[#1A3A5C] font-semibold">
                                <tr>
                                  <th className="text-left px-3 py-1.5">N° CEL</th>
                                  <th className="text-left px-3 py-1.5">Committente</th>
                                  <th className="text-left px-3 py-1.5">Oggetto</th>
                                  <th className="text-left px-3 py-1.5">Inizio</th>
                                  <th className="text-left px-3 py-1.5">Fine</th>
                                  <th className="text-right px-3 py-1.5">Originale €</th>
                                  <th className="text-right px-3 py-1.5">Ind. fine lav.</th>
                                  <th className="text-right px-3 py-1.5">Rivalutato ISTAT €</th>
                                  {risultato.incremento_icp > 0 && <th className="text-right px-3 py-1.5">ICP {risultato.incremento_icp}% €</th>}
                                  <th className="text-right px-3 py-1.5">Finale €</th>
                                  <th className="text-right px-3 py-1.5">Progressivo €</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lavori.map((lav, i) => {
                                  progSub += lav.importo_finale || 0;
                                  const isPunta = i < nPuntaSub;
                                  return (
                                    <tr key={lav.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                      <td className="px-3 py-1.5 text-gray-500">{lav.numero_cel || '—'}</td>
                                      <td className="px-3 py-1.5 text-gray-600 max-w-[120px] truncate">{lav.ente_appaltante || '—'}</td>
                                      <td className="px-3 py-1.5 max-w-[180px] truncate">{isPunta && <span className="text-yellow-500 mr-1">★</span>}{lav.lavoro_diretto && <span className="bg-amber-100 text-amber-700 text-xs px-1 py-0.5 rounded font-semibold mr-1">Dir.</span>}{lav.oggetto || '—'}</td>
                                      <td className="px-3 py-1.5 whitespace-nowrap">{fmtData(lav.data_inizio)}</td>
                                      <td className="px-3 py-1.5 whitespace-nowrap">{fmtData(lav.data_fine)}</td>
                                      <td className="px-3 py-1.5 text-right">{fmt(lav.importo_originale)}</td>
                                      <td className="px-3 py-1.5 text-right text-gray-500">{lav.indice_usato ?? '—'}</td>
                                      <td className="px-3 py-1.5 text-right">{fmt(lav.importo_rivalutato)}</td>
                                      {risultato.incremento_icp > 0 && <td className="px-3 py-1.5 text-right text-purple-600">{fmt(lav.importo_icp)}</td>}
                                      <td className="px-3 py-1.5 text-right font-medium">{fmt(lav.importo_finale)}</td>
                                      <td className="px-3 py-1.5 text-right text-[#2E6096] font-semibold">{fmt(progSub)}</td>
                                    </tr>
                                  );
                                })}
                                <tr className="bg-[#EEF4FB] font-semibold text-[#1A3A5C]">
                                  <td className="px-3 py-1.5" colSpan={risultato.incremento_icp > 0 ? 10 : 9}>Totale {subcat}</td>
                                  <td className="px-3 py-1.5 text-right">{fmt(progSub)}</td>
                                </tr>
                              </tbody>
                            </table>
                            </div>
                          </div>
                        );
                      })}
                      {g.esclusi.length > 0 && (
                        <div className="ml-4 mt-2 text-xs text-gray-400 italic">
                          {g.esclusi.map(lav => (
                            <div key={lav.id} className="py-0.5">Escluso ({lav.sottocategoria || '—'}): {lav.oggetto || lav.ente_appaltante || '—'} — {lav.motivo_esclusione}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                // ── Categorie normali ────────────────────────────────────
                const importiOrd = g.inclusi.map(l => l.importo_finale || 0);
                const { n: nPunta, nota: notaPunta } = isSpeciale ? { n: 0, nota: '' } : calcolaNPunta(importiOrd, info?.classifica);
                let progressivo = 0;
                return (
                  <div key={cat} className="mb-6">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-bold text-[#1A3A5C] text-sm">{cat}</span>
                      {info && <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${coloreClassifica(info.classifica)}`}>{info.classifica ? `Class. ${info.classifica}` : 'Non qualificata'}</span>}
                      {info && <span className="text-xs text-gray-400">{info.regola}</span>}
                      {!isSpeciale && notaPunta && <span className="text-xs text-yellow-600">{notaPunta}</span>}
                    </div>
                    <div className="overflow-x-auto">
                    <table className="w-full text-xs min-w-[600px]">
                      <thead className="bg-[#EEF4FB] text-[#1A3A5C] font-semibold">
                        <tr>
                          <th className="text-left px-3 py-1.5">N° CEL</th>
                          <th className="text-left px-3 py-1.5">Committente</th>
                          <th className="text-left px-3 py-1.5">Oggetto</th>
                          <th className="text-left px-3 py-1.5">Inizio</th>
                          <th className="text-left px-3 py-1.5">Fine</th>
                          <th className="text-right px-3 py-1.5">Originale €</th>
                          <th className="text-right px-3 py-1.5">Ind. fine lav.</th>
                          <th className="text-right px-3 py-1.5">Rivalutato ISTAT €</th>
                          {risultato.incremento_icp > 0 && <th className="text-right px-3 py-1.5">ICP {risultato.incremento_icp}% €</th>}
                          <th className="text-right px-3 py-1.5">Finale €</th>
                          <th className="text-right px-3 py-1.5">Progressivo €</th>
                          <th className="text-center px-3 py-1.5">Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.inclusi.map((lav, i) => {
                          progressivo += lav.importo_finale || 0;
                          const isPunta = !isSpeciale && i < nPunta;
                          return (
                            <tr key={lav.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-3 py-1.5 text-gray-500">{lav.numero_cel || '—'}</td>
                              <td className="px-3 py-1.5 text-gray-600 max-w-[120px] truncate">{lav.ente_appaltante || '—'}</td>
                              <td className="px-3 py-1.5 max-w-[180px] truncate">{isPunta && <span className="text-yellow-500 mr-1">★</span>}{lav.oggetto || '—'}</td>
                              <td className="px-3 py-1.5 whitespace-nowrap">{fmtData(lav.data_inizio)}</td>
                              <td className="px-3 py-1.5 whitespace-nowrap">{fmtData(lav.data_fine)}</td>
                              <td className="px-3 py-1.5 text-right">{fmt(lav.importo_originale)}</td>
                              <td className="px-3 py-1.5 text-right text-gray-500">{lav.indice_usato ?? '—'}</td>
                              <td className="px-3 py-1.5 text-right">{fmt(lav.importo_rivalutato)}</td>
                              {risultato.incremento_icp > 0 && <td className="px-3 py-1.5 text-right text-purple-600">{fmt(lav.importo_icp)}</td>}
                              <td className="px-3 py-1.5 text-right font-medium">{fmt(lav.importo_finale)}</td>
                              <td className="px-3 py-1.5 text-right text-[#2E6096] font-semibold">{fmt(progressivo)}</td>
                              <td className="px-3 py-1.5 text-center text-gray-400">{isPunta ? 'punta' : ''}</td>
                            </tr>
                          );
                        })}
                        <tr className="bg-[#EEF4FB] font-semibold text-[#1A3A5C]">
                          <td className="px-3 py-1.5" colSpan={risultato.incremento_icp > 0 ? 10 : 9}>Totale {cat}</td>
                          <td className="px-3 py-1.5 text-right">{fmt(progressivo)}</td>
                          <td />
                        </tr>
                        {g.esclusi.map(lav => (
                          <tr key={lav.id} className="bg-red-50 text-gray-400 text-xs">
                            <td className="px-3 py-1">{lav.numero_cel || '—'}</td>
                            <td className="px-3 py-1 italic">{lav.ente_appaltante || '—'}</td>
                            <td className="px-3 py-1 italic max-w-[180px] truncate">{lav.oggetto || '—'}</td>
                            <td className="px-3 py-1">{fmtData(lav.data_inizio)}</td>
                            <td className="px-3 py-1">{fmtData(lav.data_fine)}</td>
                            <td className="px-3 py-1 text-right">{fmt(lav.importo_originale)}</td>
                            <td colSpan={risultato.incremento_icp > 0 ? 5 : 4} className="px-3 py-1 text-red-400 italic">Escluso — {lav.motivo_esclusione}</td>
                            <td />
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                  );
                })}

                  {/* Barra navigazione inferiore */}
                  {catList.length > 1 && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                      <button onClick={() => setCatIndex(i => Math.max(0, i - 1))} disabled={idxSafe === 0}
                        className="flex items-center gap-1 px-4 py-1.5 rounded-lg border border-gray-300 text-sm disabled:opacity-30 hover:bg-gray-50 transition">
                        ‹ {idxSafe > 0 ? catList[idxSafe - 1] : ''}
                      </button>
                      <span className="text-xs text-gray-400">{idxSafe + 1} / {catList.length}</span>
                      <button onClick={() => setCatIndex(i => Math.min(catList.length - 1, i + 1))} disabled={idxSafe === catList.length - 1}
                        className="flex items-center gap-1 px-4 py-1.5 rounded-lg border border-gray-300 text-sm disabled:opacity-30 hover:bg-gray-50 transition">
                        {idxSafe < catList.length - 1 ? catList[idxSafe + 1] : ''} ›
                      </button>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab Attestazione SOA ────────────────────────────────────────────────
function statoScadenza(dataScadenza) {
  if (!dataScadenza) return null;
  const giorni = Math.ceil((new Date(dataScadenza) - new Date()) / (1000 * 60 * 60 * 24));
  if (giorni < 0)    return { label: `Scaduta da ${Math.abs(giorni)} giorni`, cls: 'bg-red-100 text-red-700 border-red-200' };
  if (giorni <= 180) return { label: `Scade tra ${giorni} giorni`, cls: 'bg-yellow-100 text-yellow-700 border-yellow-200' };
  return               { label: `Valida — scade tra ${giorni} giorni`, cls: 'bg-green-100 text-green-700 border-green-200' };
}

const CATEGORIE_SOA = [
  'OG1','OG2','OG3','OG4','OG5','OG6','OG7','OG8','OG9','OG10','OG11','OG12','OG13',
  'OS1','OS2','OS2-A','OS2-B','OS3','OS4','OS5','OS6','OS7','OS8','OS9','OS10',
  'OS11','OS12','OS12-A','OS12-B','OS13','OS14','OS15','OS16','OS17','OS18',
  'OS18-A','OS18-B','OS19','OS20','OS20-A','OS20-B','OS21','OS22','OS23','OS24','OS25','OS26',
  'OS27','OS28','OS29','OS30','OS31','OS32','OS33','OS34','OS35',
];
const CLASSIFICHE_SOA = ['I','II','III','III-bis','IV','IV-bis','V','VI','VII','VIII'];

function TabAttestazione({ impresaId, impresa, onAggiornata }) {
  const [form, setForm] = useState({
    att_numero:        impresa?.att_numero        || '',
    att_organismo:     impresa?.att_organismo     || '',
    att_tipo_scadenza: impresa?.att_tipo_scadenza || '',
    att_data_rilascio: impresa?.att_data_rilascio ? new Date(impresa.att_data_rilascio).toISOString().slice(0, 10) : '',
    att_data_scadenza: impresa?.att_data_scadenza ? new Date(impresa.att_data_scadenza).toISOString().slice(0, 10) : '',
    att_note:          impresa?.att_note          || '',
  });
  const [saving, setSaving] = useState(false);
  const [salvato, setSalvato] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const [categorie, setCategorie] = useState(impresa?.categorie_attestazione || []);
  const [nuovaCat, setNuovaCat] = useState({ categoria: '', classifica: '' });
  const [aggiungendo, setAggiungendo] = useState(false);
  const [editingCatId, setEditingCatId] = useState(null);
  const [editingCatForm, setEditingCatForm] = useState({ categoria: '', classifica: '' });

  const stato = statoScadenza(form.att_data_scadenza);

  async function salva(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`/api/imprese-soa/${impresaId}`, form);
      setSalvato(true);
      setTimeout(() => setSalvato(false), 2500);
      onAggiornata();
    } finally {
      setSaving(false);
    }
  }

  async function aggiungiCategoria() {
    if (!nuovaCat.categoria || !nuovaCat.classifica) return;
    setAggiungendo(true);
    try {
      const r = await axios.post(`/api/imprese-soa/${impresaId}/categorie`, nuovaCat);
      setCategorie(c => [...c, r.data].sort((a, b) => a.categoria.localeCompare(b.categoria)));
      setNuovaCat({ categoria: '', classifica: '' });
    } finally {
      setAggiungendo(false);
    }
  }

  async function eliminaCategoria(catId) {
    await axios.delete(`/api/imprese-soa/${impresaId}/categorie/${catId}`);
    setCategorie(c => c.filter(x => x.id !== catId));
  }

  function apriModificaCategoria(cat) {
    setEditingCatId(cat.id);
    setEditingCatForm({ categoria: cat.categoria, classifica: cat.classifica });
  }

  async function salvaModificaCategoria(catId) {
    const r = await axios.put(`/api/imprese-soa/${impresaId}/categorie/${catId}`, editingCatForm);
    setCategorie(c => c.map(x => x.id === catId ? r.data : x).sort((a, b) => a.categoria.localeCompare(b.categoria)));
    setEditingCatId(null);
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow p-6 max-w-xl">
        <h3 className="font-bold text-[#1A3A5C] mb-5">Attestazione SOA</h3>

        {stato && (
          <div className={`mb-5 rounded-lg px-4 py-3 border text-sm font-medium ${stato.cls}`}>
            {stato.label}
            {form.att_tipo_scadenza && <span className="ml-2 font-normal opacity-75">· scadenza {form.att_tipo_scadenza}</span>}
          </div>
        )}

        <form onSubmit={salva} className="space-y-4">
          <Campo label="N° Attestazione" value={form.att_numero} onChange={v => set('att_numero', v)} />
          <Campo label="Organismo di attestazione" value={form.att_organismo} onChange={v => set('att_organismo', v)} />
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tipo scadenza</label>
            <select
              value={form.att_tipo_scadenza}
              onChange={e => set('att_tipo_scadenza', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
            >
              <option value="">— seleziona —</option>
              <option value="triennale">Triennale (3 anni)</option>
              <option value="quinquennale">Quinquennale (5 anni)</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Campo label="Data rilascio" type="date" value={form.att_data_rilascio} onChange={v => set('att_data_rilascio', v)} />
            <Campo label="Data scadenza" type="date" value={form.att_data_scadenza} onChange={v => set('att_data_scadenza', v)} />
          </div>
          <Campo label="Note" value={form.att_note} onChange={v => set('att_note', v)} />
          <div className="flex items-center gap-3 pt-1">
            <button type="submit" disabled={saving}
              className="bg-[#1A3A5C] text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-[#2E6096] transition">
              {saving ? 'Salvo...' : 'Salva'}
            </button>
            {salvato && <span className="text-green-600 text-sm font-medium">✓ Salvato</span>}
          </div>
        </form>
      </div>

      {/* Categorie e classifiche */}
      <div className="bg-white rounded-xl shadow p-6 max-w-xl">
        <h3 className="font-bold text-[#1A3A5C] mb-4">Categorie e classifiche attestate</h3>

        {categorie.length > 0 && (
          <table className="w-full text-sm mb-4">
            <thead className="bg-[#EEF4FB] text-[#1A3A5C] text-xs font-semibold">
              <tr>
                <th className="text-left px-3 py-2">Categoria</th>
                <th className="text-left px-3 py-2">Classifica</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {categorie.map((c, i) => (
                <tr key={c.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  {editingCatId === c.id ? (
                    <>
                      <td className="px-3 py-1.5">
                        <select
                          value={editingCatForm.categoria}
                          onChange={e => setEditingCatForm(f => ({ ...f, categoria: e.target.value }))}
                          className="border border-[#2E6096] rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#2E6096]"
                        >
                          {CATEGORIE_SOA.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-1.5">
                        <select
                          value={editingCatForm.classifica}
                          onChange={e => setEditingCatForm(f => ({ ...f, classifica: e.target.value }))}
                          className="border border-[#2E6096] rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-[#2E6096]"
                        >
                          {CLASSIFICHE_SOA.map(cl => <option key={cl} value={cl}>{cl}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-1.5 text-right whitespace-nowrap">
                        <button onClick={() => salvaModificaCategoria(c.id)} className="text-[#2E6096] hover:text-[#1A3A5C] text-xs font-medium mr-3">Salva</button>
                        <button onClick={() => setEditingCatId(null)} className="text-gray-400 hover:text-gray-600 text-xs">Annulla</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2 font-medium text-[#1A3A5C]">{c.categoria}</td>
                      <td className="px-3 py-2 text-gray-700">{c.classifica}</td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => apriModificaCategoria(c)} className="text-[#2E6096] hover:text-[#1A3A5C] text-xs font-medium mr-3">Modifica</button>
                        <button onClick={() => eliminaCategoria(c.id)} className="text-red-500 hover:text-red-700 text-xs font-medium">Elimina</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="flex gap-2 items-end flex-wrap">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Categoria</label>
            <select
              value={nuovaCat.categoria}
              onChange={e => setNuovaCat(n => ({ ...n, categoria: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
            >
              <option value="">— seleziona —</option>
              {CATEGORIE_SOA.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Classifica</label>
            <select
              value={nuovaCat.classifica}
              onChange={e => setNuovaCat(n => ({ ...n, classifica: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E6096]"
            >
              <option value="">— seleziona —</option>
              {CLASSIFICHE_SOA.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button
            onClick={aggiungiCategoria}
            disabled={aggiungendo || !nuovaCat.categoria || !nuovaCat.classifica}
            className="bg-[#1A3A5C] text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-[#2E6096] transition"
          >
            {aggiungendo ? 'Aggiungo...' : '+ Aggiungi'}
          </button>
        </div>
      </div>
    </div>
  );
}
